import React from 'react';
import { CategoryTreeSelect } from './CategoryTreeSelect';
import { StyleFacetSelect } from './StyleFacetSelect';
import { ToolSoftTagInput } from './ToolSoftTagInput';
import { ChevronDown, ChevronUp, Tags } from 'lucide-react';
import { flattenTagStateToNames } from './tagStateUtils';

/**
 * NEW SelectTags for Phase 1: Layered Index System
 *
 * Replaces the old 4-dim flat tags (CATEGORY, TOOL, STYLE, TOPIC)
 * with:
 *   1. Hierarchical Category Tree (1 primary + 0~2 secondary)
 *   2. Style Facets (curated set, no custom creation)
 *   3. Tool Soft Tags (user-creatable with suggestions)
 *
 * Topic/Culture tags are REMOVED from the publishing flow.
 */

export interface NewTagState {
    primaryCategory: string | null;
    secondaryCategories: string[];
    toolTags: string[];
    styleFacets: string[];
}

interface SelectTagsProps {
    value: NewTagState;
    onChange: (value: NewTagState) => void;
    className?: string;
    filterMode?: boolean;
    showCollapsed?: boolean;
}

export const SelectTags: React.FC<SelectTagsProps> = ({
    value,
    onChange,
    className = '',
    filterMode = false,
    showCollapsed = true,
}) => {
    const { primaryCategory, secondaryCategories, toolTags, styleFacets } = value;

    const totalCount =
        (primaryCategory ? 1 : 0) +
        secondaryCategories.length +
        toolTags.length +
        styleFacets.length;

    const allSelectedNames = flattenTagStateToNames(value);

    const setField = <K extends keyof NewTagState>(key: K, val: NewTagState[K]) => {
        onChange({ ...value, [key]: val });
    };

    const handlePrimaryCategoryChange = (categoryId: string | null) => {
        onChange({
            ...value,
            primaryCategory: categoryId,
            ...(categoryId === null ? { secondaryCategories: [] } : {}),
        });
    };

    // Publish mode: collapsed by default (must be before any early return)
    const [isExpanded, setIsExpanded] = React.useState(false);

    // Filter mode: always expanded
    if (filterMode) {
        return (
            <div className={`space-y-6 ${className}`}>
                <div>
                    <CategoryTreeSelect
                        primaryCategory={primaryCategory}
                        secondaryCategories={secondaryCategories}
                        onPrimaryChange={handlePrimaryCategoryChange}
                        onSecondaryChange={(ids) => setField('secondaryCategories', ids)}
                        label="业务分类"
                    />
                </div>
                <div className="h-px bg-gray-50" />
                <div>
                    <StyleFacetSelect
                        selectedFacets={styleFacets}
                        onChange={(ids) => setField('styleFacets', ids)}
                        label="风格倾向"
                    />
                </div>
                <div className="h-px bg-gray-50" />
                <div>
                    <ToolSoftTagInput
                        selectedTools={toolTags}
                        onChange={(tools) => setField('toolTags', tools)}
                        label="工具/模型"
                    />
                </div>
            </div>
        );
    }

    if (showCollapsed) {
        return (
            <div className={className}>
                {/* Collapsed Summary */}
                <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="w-full flex items-center justify-between py-3 px-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
                >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Tags className="w-4 h-4 text-gray-400 shrink-0" />
                        {totalCount > 0 ? (
                            <div className="flex flex-wrap gap-1.5 min-w-0">
                                {allSelectedNames.slice(0, 6).map((name, i) => (
                                    <span
                                        key={`${name}-${i}`}
                                        className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-600 border border-blue-100"
                                    >
                                        {name}
                                    </span>
                                ))}
                                {allSelectedNames.length > 6 && (
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                        +{allSelectedNames.length - 6}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <span className="text-sm text-gray-400">点击选择分类、风格、工具</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1.5 ml-3 shrink-0">
                        {totalCount > 0 && (
                            <span className="text-xs text-gray-400">{totalCount}项</span>
                        )}
                        {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                    </div>
                </button>

                {/* Expanded Panel */}
                {isExpanded && (
                    <div className="mt-3 p-5 rounded-xl border border-gray-100 bg-white space-y-5 animate-in slide-in-from-top-2 duration-200">
                        <div>
                            <CategoryTreeSelect
                                primaryCategory={primaryCategory}
                                secondaryCategories={secondaryCategories}
                                onPrimaryChange={handlePrimaryCategoryChange}
                                onSecondaryChange={(ids) => setField('secondaryCategories', ids)}
                            />
                        </div>

                        <div className="h-px bg-gray-100" />

                        <div>
                            <StyleFacetSelect
                                selectedFacets={styleFacets}
                                onChange={(ids) => setField('styleFacets', ids)}
                            />
                        </div>

                        <div className="h-px bg-gray-100" />

                        <div>
                            <ToolSoftTagInput
                                selectedTools={toolTags}
                                onChange={(tools) => setField('toolTags', tools)}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() => setIsExpanded(false)}
                            className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-1"
                        >
                            <ChevronUp className="w-3.5 h-3.5" />
                            收起
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // Always expanded mode
    return (
        <div className={`space-y-7 ${className}`}>
            <div>
                <CategoryTreeSelect
                    primaryCategory={primaryCategory}
                    secondaryCategories={secondaryCategories}
                    onPrimaryChange={handlePrimaryCategoryChange}
                    onSecondaryChange={(ids) => setField('secondaryCategories', ids)}
                />
            </div>
            <div className="border-t border-slate-100 pt-6">
                <StyleFacetSelect
                    selectedFacets={styleFacets}
                    onChange={(ids) => setField('styleFacets', ids)}
                />
            </div>
            <div className="border-t border-slate-100 pt-6">
                <ToolSoftTagInput
                    selectedTools={toolTags}
                    onChange={(tools) => setField('toolTags', tools)}
                />
            </div>
        </div>
    );
};
